import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  SimpleGrid,
  Icon,
  Badge,
  useDisclosure,
  useBreakpointValue,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Container,
} from '@chakra-ui/react';
import {GiWallet} from 'react-icons/gi';
import {

  FaPlus,
  FaMinus,
  FaHistory,
  FaClock,
  FaShieldAlt,
  FaUsers
} from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';
import WalletBalance from './WalletBalance';
import AddFundsModal from './AddFundsModal';
import PayoutModal from './PayoutModal';
import TransactionHistory from './TransactionHistory';
import PendingEarnings from './PendingEarnings';
import WalletStats from './WalletStats';
import MerchantWalletControls from './MerchantWalletControls';
import ViewOnlyWalletSummary from './ViewOnlyWalletSummary';
import LinkedArtistsSummary from './LinkedArtistsSummary';

const WalletDashboardContent = () => {
  const { walletSummary, loading, error, refreshWallet, walletData, getWalletContext, fetchWalletDashboard } = useWalletContext();
  const navigate = useNavigate();

  // Responsive values
  const containerMaxW = useBreakpointValue({ base: 'full', lg: '6xl' });
  const spacing = useBreakpointValue({ base: 4, md: 6 });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });

  // Modal states
  const { isOpen: isAddFundsOpen, onOpen: onAddFundsOpen, onClose: onAddFundsClose } = useDisclosure();
  const { isOpen: isPayoutOpen, onOpen: onPayoutOpen, onClose: onPayoutClose } = useDisclosure();

  // Get wallet context for conditional rendering - use wallet data context for reactivity
  const walletContext = React.useMemo(() => {
    // Use context from wallet data if available (more reactive to changes)
    return walletData?.wallet_context || getWalletContext();
  }, [walletData?.wallet_context, getWalletContext]);

  const isViewOnly = walletContext.is_view_only;
  const isSwitchedArtist = walletContext.type === 'switched_artist';
  const isMerchant = walletContext.type === 'merchant';

  // Fetch wallet data when component mounts or context changes
  React.useEffect(() => {
    fetchWalletDashboard(true); // Force fresh fetch
  }, [fetchWalletDashboard]);

  // Loading state
  if (loading && !walletSummary) {
    return (
      <Container maxW={containerMaxW} py={8}>
        <Box bg="dark.500" borderRadius="xl" p={8} minH="500px">
          <Flex justify="center" align="center" h="400px">
            <VStack spacing={4}>
              <Spinner size="xl" color="primary.500" thickness="4px" />
              <Text color="gray.400" fontSize="lg">Loading your wallet...</Text>
            </VStack>
          </Flex>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error && !walletSummary) {
    return (
      <Container maxW={containerMaxW} py={8}>
        <Box bg="dark.500" borderRadius="xl" p={8}>
          <Alert status="error" borderRadius="lg" bg="red.900" borderColor="red.600">
            <AlertIcon color="red.300" />
            <VStack align="start" spacing={2}>
              <Text fontWeight="bold" color="red.200">Failed to load wallet</Text>
              <Text fontSize="sm" color="red.300">{error}</Text>
              <Button size="sm" colorScheme="red" variant="outline" onClick={refreshWallet}>
                Try Again
              </Button>
            </VStack>
          </Alert>
        </Box>
      </Container>
    );
  }

  const handleAddFundsSuccess = () => {
    onAddFundsClose();
    refreshWallet();
  };

  const handlePayoutSuccess = () => {
    onPayoutClose();
    refreshWallet();
  };

  // If this is a switched artist (view-only mode), show simplified UI

  if (isViewOnly && isSwitchedArtist) {
    return (
      <Container maxW={containerMaxW} py={spacing}>
        <VStack spacing={spacing} align="stretch">
          {/* Simplified Header for View-Only */}
          <Box bg="secondary.100" borderRadius="xl" p={6}>
            <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
              <HStack spacing={4}>
                <Box p={2} bg="blue.500" borderRadius="lg" boxShadow="lg">
                  <Icon as={GiWallet} boxSize={6} color="white" />
                </Box>
                <VStack align="start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="primary.100">
                    Artist Wallet (View Only)
                  </Text>
                  <Text fontSize="sm" color="secondary.900">
                    Earnings overview and transaction history
                  </Text>
                </VStack>
              </HStack>
              <Badge colorScheme="blue" variant="solid" fontSize="sm" px={3} py={1}>
                View Only Mode
              </Badge>
            </Flex>
          </Box>

          {/* View-Only Content */}
          <ViewOnlyWalletSummary />

          {/* Transaction History (Read-Only) */}
          <Box bg="dark.500" borderRadius="xl" p={6}>
            <TransactionHistory readOnly={true} />
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW={containerMaxW} py={spacing}>
      <VStack spacing={spacing} align="stretch">
        {/* Header Section */}
        <Box bg="secondary.100"  borderRadius="xl" p={6}>
          <Flex 
            justify="space-between" 
            align="center" 
            direction={{ base: 'column', md: 'row' }}
            gap={4}
          >
            <HStack spacing={4}>
              <Box 
                p={2} 
                bg="secondary.900"
                borderRadius="lg"
                boxShadow="lg"
              >
                <Icon as={GiWallet} boxSize={6} color="dark.900" />
              </Box>
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color="primary.100">
                  My Wallet
                </Text>
                <Text fontSize="sm" color="secondary.900">
                  Secure digital wallet with instant transactions
                </Text>
              </VStack>
            </HStack>
            
            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<FaPlus />}
                variant="solid"
                bg="secondary.500"
                color="dark.900"
                _hover={{ bg: 'secondary.600' }}
                onClick={onAddFundsOpen}
                size={buttonSize}
                borderRadius="lg"
              >
                Add Funds
              </Button>
              <Button
                leftIcon={<FaMinus />}
                variant="outline"
                borderColor="dark.900"
                color="primary.500"
                _hover={{ bg: 'secondary.500', color: 'dark.900' }}
                onClick={onPayoutOpen}
                isDisabled={!walletSummary?.available_balance || walletSummary.available_balance < 10}
                size={buttonSize}
                borderRadius="lg"
              >
                Withdraw
              </Button>
              {isMerchant && !isSwitchedArtist && (
                <Button
                  leftIcon={<FaUsers />}
                  variant="outline"
                  borderColor="primary.500"
                  color="primary.500"
                  _hover={{ bg: 'primary.500', color: 'dark.900' }}
                  onClick={() => navigate('/merchant/artists')}
                  size={buttonSize}
                  borderRadius="lg"
                >
                  Manage Artists
                </Button>
              )}
            </HStack>
          </Flex>
        </Box>

        {/* Linked Artists Summary (when merchant viewing own wallet) */}
        {isMerchant && !isSwitchedArtist && walletData?.linked_artists_summary && (
          <LinkedArtistsSummary onClick={() => navigate('/merchant/artists')} />
        )}

        {/* Merchant Controls for Artists (when viewing artist wallet) */}
        {isMerchant && walletContext.artist_id && (
          <MerchantWalletControls
            artistId={walletContext.artist_id}
            artistName={walletData?.artist_name || "Artist"}
          />
        )}

        {/* Main Content Grid */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={spacing}>
          {/* Balance & Stats Column */}
          <VStack spacing={spacing}>
            <WalletBalance
              walletSummary={walletSummary}
              onAddFunds={onAddFundsOpen}
              onPayout={onPayoutOpen}
            />
            <WalletStats walletSummary={walletSummary} />
          </VStack>

          {/* Transactions Column */}
          <Box gridColumn={{ base: 1, lg: 'span 2' }}>
            <Box bg="secondary.100"  borderRadius="xl" p={6} h="full">
              <Tabs variant="soft-rounded" colorScheme="primary" size="md">
                <TabList 
                  mb={6} 
                  bg="dark.100" 
                  p={1}
                  overflowX="auto"
                  css={{
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    '-ms-overflow-style': 'none',
                    'scrollbar-width': 'none',
                  }}
                >
                  <Tab 
                    flex={{ base: "none", md: 1 }}
                    minW={{ base: "120px", md: "auto" }}
                    borderRadius="none"
                    color="primary.500"
                    mx={1}
                    px={{ base: 2, md: 4 }}
                    fontSize={{ base: "sm", md: "md" }}
                    _selected={{ 
                      bg: 'secondary.500', 
                      color: 'dark.900'
                    }}
                  >
                    <HStack spacing={{ base: 1, md: 2 }}>
                      <Icon as={FaHistory} boxSize={{ base: 3, md: 4 }} />
                      <Text display={{ base: "none", sm: "block" }}>Transactions</Text>
                      <Text display={{ base: "block", sm: "none" }}>History</Text>
                    </HStack>
                  </Tab>
                  <Tab 
                    flex={{ base: "none", md: 1 }}
                    minW={{ base: "120px", md: "auto" }}
                    borderRadius="none"
                    color="primary.500"
                    mx={1}
                    px={{ base: 2, md: 4 }}
                    fontSize={{ base: "sm", md: "md" }}
                    _selected={{ 
                      bg: 'secondary.500', 
                      color: 'dark.900'
                    }}
                  >
                    <HStack spacing={{ base: 1, md: 2 }}>
                      <Icon as={FaClock} boxSize={{ base: 3, md: 4 }} />
                      <Text>Pending</Text>
                      {walletSummary?.pending_earnings_count > 0 && (
                        <Badge 
                          colorScheme="yellow" 
                          borderRadius="full"
                          fontSize="xs"
                          px={1}
                          minW="16px"
                          h="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {walletSummary.pending_earnings_count}
                        </Badge>
                      )}
                    </HStack>
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel p={0}>
                    <TransactionHistory />
                  </TabPanel>
                  <TabPanel p={0}>
                    <PendingEarnings />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Box>
        </SimpleGrid>

        {/* Security Notice */}
        <Box 
          bg="blue.900" 
          borderRadius="xl" 
          p={6} 
          border="1px solid"
          borderColor="blue.700"
          position="relative"
          overflow="hidden"
        >
          {/* Gradient overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgGradient="linear(45deg, blue.900, blue.800)"
            opacity={0.7}
            zIndex={0}
          />
          
          <HStack spacing={4} position="relative" zIndex={1}>
            <Box p={3} bg="blue.600" borderRadius="lg">
              <Icon as={FaShieldAlt} color="blue.100" boxSize={5} />
            </Box>
            <VStack align="start" spacing={2}>
              <Text fontSize="lg" fontWeight="bold" color="blue.100">
                Your wallet is protected
              </Text>
              <Text fontSize="sm" color="blue.200" maxW="2xl">
                All transactions are encrypted with bank-level security. Earnings are held for 5 business days 
                for enhanced protection against fraud and chargebacks.
              </Text>
            </VStack>
          </HStack>
        </Box>
      </VStack>

      {/* Modals */}
      <AddFundsModal
        isOpen={isAddFundsOpen}
        onClose={onAddFundsClose}
        onSuccess={handleAddFundsSuccess}
      />
      
      <PayoutModal
        isOpen={isPayoutOpen}
        onClose={onPayoutClose}
        onSuccess={handlePayoutSuccess}
        availableBalance={walletSummary?.available_balance || 0}
      />
    </Container>
  );
};

export default React.memo(WalletDashboardContent);